import type { ReactNode } from 'react';

export interface VestingBarLabels {
  readonly unlocked?: string;
  readonly vesting?: string;
}

export interface VestingBarProps {
  /** Ratio 0..1 of unlocked portion. Clamped. */
  readonly unlocked: number;
  /** Pre-formatted unlocked amount (mono rendering). */
  readonly unlockedAmount: ReactNode;
  /** Pre-formatted vesting amount. */
  readonly vestingAmount: ReactNode;
  /** Translatable labels. Defaults EN. */
  readonly labels?: VestingBarLabels;
  readonly className?: string;
}

const DEFAULT_LABELS: Required<VestingBarLabels> = {
  unlocked: 'Unlocked',
  vesting: 'Vesting',
};

/**
 * Vesting progress indicator. Top row: uppercase labels "Unlocked" / "Vesting".
 * Middle: 2px track with primary-color fill at `unlocked` ratio.
 * Bottom: mono amounts, unlocked in text-primary, vesting in text-secondary.
 *
 * Reference: docs/design_handoff/.../primitives.jsx VestingBar.
 */
export function VestingBar({
  unlocked,
  unlockedAmount,
  vestingAmount,
  labels,
  className = '',
}: VestingBarProps): ReactNode {
  const ratio = Math.max(0, Math.min(1, unlocked));
  const percent = Math.round(ratio * 100);
  const effective = { ...DEFAULT_LABELS, ...(labels ?? {}) };

  return (
    <div className={['mt-[14px]', className].filter(Boolean).join(' ')}>
      <div className="mb-2 flex justify-between text-[10px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
        <span>{effective.unlocked}</span>
        <span>{effective.vesting}</span>
      </div>
      <div className="h-[2px] overflow-hidden rounded-full bg-border-strong">
        <div
          data-vesting-fill
          className="h-full bg-text-primary"
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="mt-1.5 flex justify-between">
        <div className="font-mono text-[11px] font-medium text-text-primary tabular-nums">
          {unlockedAmount}
        </div>
        <div className="font-mono text-[11px] text-text-secondary tabular-nums">
          {vestingAmount}
        </div>
      </div>
    </div>
  );
}
